/**
 * Single source of truth for the ambient ticker bar.
 * Subscribes to one WebSocket (default ticker SPX) and derives spot,
 * Δ%, flip, king, regime, and connection age from the latest frame.
 *
 * `prev_close` is fetched once every 5 minutes (matches the server-side
 * cache TTL) and used to compute Δ. If unavailable, Δ is null.
 */

import { useEffect, useRef, useState } from 'react';
import { useWebSocket } from './useWebSocket';
import { apiGet } from '../lib/api';

const PREV_CLOSE_POLL_MS = 5 * 60_000;

export type ConnectionStatus = 'streaming' | 'stale' | 'polling' | 'offline';

export interface AmbientTelemetry {
  ticker: string;
  spot: number | null;
  prevClose: number | null;
  spotChange: number | null;
  spotChangePct: number | null;
  flip: number | null;
  king: { strike: number; distancePct: number | null } | null;
  regime: 'POSITIVE_GAMMA' | 'NEGATIVE_GAMMA' | 'UNKNOWN';
  connection: ConnectionStatus;
  lastUpdate: Date | null;
}

interface PrevCloseResponse {
  ticker: string;
  prev_close: number | null;
  asof: number;
}

interface UseAmbientTelemetryOptions {
  ticker?: string;
}

export function useAmbientTelemetry({ ticker = 'SPX' }: UseAmbientTelemetryOptions = {}): AmbientTelemetry {
  // We deliberately don't read `isConnected` here. Raw WS state flips
  // on every transient reconnect (NAT timeouts, wifi handoffs, upstream
  // proxy idle close), which makes the ambient status chip flicker
  // STREAMING → OFFLINE → STREAMING within seconds even though pipeline
  // data was never actually stale. We derive freshness from
  // lastUpdateTime alone — the chip only goes OFFLINE when data is
  // genuinely old.
  const { latestFrame, lastUpdateTime } = useWebSocket(ticker);
  const [prevClose, setPrevClose] = useState<number | null>(null);
  const cancelledRef = useRef(false);

  // Poll prev_close every 5 min.
  useEffect(() => {
    cancelledRef.current = false;
    const ctrl = new AbortController();

    const tick = async () => {
      try {
        const data = await apiGet<PrevCloseResponse>(`/spot/${ticker}/prev_close`, ctrl.signal);
        if (!cancelledRef.current) setPrevClose(data.prev_close);
      } catch {
        /* keep last value; degrade silently */
      }
    };

    tick();
    const id = setInterval(tick, PREV_CLOSE_POLL_MS);
    return () => {
      cancelledRef.current = true;
      ctrl.abort();
      clearInterval(id);
    };
  }, [ticker]);

  // Derive metrics
  const spot = latestFrame?.spot_price ?? null;
  const flip = latestFrame?.gex_flip_point ?? null;

  const spotChange = spot != null && prevClose != null && prevClose !== 0 ? spot - prevClose : null;
  const spotChangePct = spot != null && prevClose != null && prevClose !== 0 ? (spot - prevClose) / prevClose : null;

  let regime: AmbientTelemetry['regime'] = 'UNKNOWN';
  if (spot != null && flip != null) {
    regime = spot >= flip ? 'POSITIVE_GAMMA' : 'NEGATIVE_GAMMA';
  }

  // King: top-magnitude node from the frame
  let king: AmbientTelemetry['king'] = null;
  if (latestFrame?.gex_nodes?.length) {
    const k = latestFrame.gex_nodes.find(n => n.node_type === 'king')
           ?? latestFrame.gex_nodes.reduce((a, b) => Math.abs(a.value) >= Math.abs(b.value) ? a : b);
    if (k && spot != null && spot !== 0) {
      king = { strike: k.strike, distancePct: (k.strike - spot) / spot };
    } else if (k) {
      king = { strike: k.strike, distancePct: null };
    }
  }

  // Connection status — purely age-based now, so transient WS
  // reconnects don't flip the chip.
  //   < 90 s   → streaming  (within one poll cycle of fresh data)
  //   < 5 min  → stale      (one missed cycle, soft warning)
  //   < 24 h   → polling    (data is from today but the live stream
  //                          hasn't pushed in a while; e.g. weekend)
  //   else     → offline    (genuinely cold, server probably down)
  // No data yet (first page load before first frame) shows offline.
  let connection: ConnectionStatus;
  if (lastUpdateTime == null) {
    connection = 'offline';
  } else {
    const ageSec = (Date.now() - lastUpdateTime.getTime()) / 1000;
    if (ageSec < 90)            connection = 'streaming';
    else if (ageSec < 300)      connection = 'stale';
    else if (ageSec < 86_400)   connection = 'polling';
    else                        connection = 'offline';
  }

  return {
    ticker,
    spot,
    prevClose,
    spotChange,
    spotChangePct,
    flip,
    king,
    regime,
    connection,
    lastUpdate: lastUpdateTime,
  };
}
